import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TopNavbar from "../../Components/Navbar/Navbar";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Spinner,
  Alert,
  Button,
  Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./StateAssetsSummary.css";
import 'bootstrap-icons/font/bootstrap-icons.css';

const API_BASE = "https://api.assetview.warrantyindia.com/api";

const StateAssetsSummary = () => {
  const navigate = useNavigate();

  const assetTypeDisplayNames = {
    'Air Conditioner - Split': 'AC - Split',
    'Air Conditioner - Window': 'AC - Window',
    'All-in-One Printer/Scanner': 'AIO Printer',
    'Almirah': 'Almirah',
    'Calculator': 'Calculator',
    'CCTV Camera': 'CCTV Camera',
    'Ceiling Fan': 'Ceiling Fan',
    'Chair': 'Chair',
    'Desk': 'Desk',
    'Desktop Computer': 'Desktop',
    'Display Rack': 'Display Rack',
    'Dustbin': 'Dustbin',
    'Electric Kettle': 'Electric Kettle',
    'Extension Board': 'Extension Board',
    'External Hard Drive': 'External HDD',
    'Inkjet Printer': 'Inkjet Printer',
    'Inverter': 'Inverter',
    'Inverter Battery': 'Inverter Battery',
    'Keyboard': 'Keyboard',
    'Laptop': 'Laptop',
    'Laptop Charger': 'Laptop Charger',
    'Laser Printer': 'Laser Printer',
    'LED Bulb': 'LED Bulb',
    'LED Tube Light': 'LED Tube Light',
    'Mobile Phone': 'Mobile Phone',
    'Mouse': 'Mouse',
    'Notice Board': 'Notice Board',
    'Pedestal Fan': 'Pedestal Fan',
    'Router': 'Router',
    'Scanner': 'Scanner',
    'SIM Card (Postpaid)': 'SIM Card',
    'Storage Cupboard': 'Storage Cupboard',
    'Table': 'Table',
    'Tablet': 'Tablet',
    'Water Bottle': 'Water Bottle',
    'Water Dispenser': 'Water Dispenser',
    'Water Jug': 'Water Jug',
    'WiFi Access Point': 'WiFi AP'
  };

  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [states, setStates] = useState([]); // [{ state: "Maharashtra", counts: { Desktop:1, Wifi:3... } }]
  const [types, setTypes] = useState([]);   // ["Desktop","Wifi",...]
  const [search, setSearch] = useState("");

  const [notUpdated, setNotUpdated] = useState([]);
const [lastSentAt, setLastSentAt] = useState(null);
const [sendingEmail, setSendingEmail] = useState(null); // Track which user is being sent

const fetchNotUpdated = async () => {
  try {
    const resp = await axios.get(`${API_BASE}/admin/assets/not-updated-since-mail`);
    if (resp.data?.ok) {
      setNotUpdated(resp.data.data || []);
      setLastSentAt(resp.data.last_sent_at);
    }
  } catch (e) {
    console.error(e);
  }
};

// Add this function
const sendIndividualReminder = async (username, employeeName) => {
  if (!window.confirm(`Send reminder email to ${employeeName}?`)) return;

  setSendingEmail(username);
  try {
    const response = await axios.post(
      `${API_BASE}/admin/mail-schedule/send-individual`,
      { username }
    );

    if (response.data?.ok) {
      alert(`✅ Email sent successfully to ${employeeName}`);
      fetchNotUpdated(); // ✅ refresh so count updates
    } else {
      alert(`❌ Failed: ${response.data?.error || "Unknown error"}`);
    }
  } catch (error) {
    console.error("Error sending email:", error);
    alert(`❌ Error sending email: ${error.response?.data?.error || error.message}`);
  } finally {
    setSendingEmail(null);
  }
};

  const fetchSummary = async () => {
    setPending(true);
    setError("");
    try {
      const resp = await axios.get(`${API_BASE}/admin/assets/state-summary`);
      if (resp.data?.ok) {
        setStates(resp.data.data?.rows || []);
        setTypes(resp.data.data?.types || []);
      } else {
        setError(resp.data?.error || "Failed to load summary");
      }
    } catch (e) {
      console.error(e);
      setError("Failed to load summary");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchNotUpdated();
  }, []);

  const filteredStates = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return states;
    return states.filter((r) =>
      String(r.state || "").toLowerCase().includes(s)
    );
  }, [states, search]);

  const onView = (stateName) => {
    navigate(`/adminassets?state=${encodeURIComponent(stateName)}`);
  };

  return (
    <>
      <TopNavbar />

      <div className="stateassets-page">
        <Container fluid className="py-4">
          <Row className="justify-content-center">
            <Col xl={11} lg={11} md={12} sm={12} xs={12}>
              <Card className="shadow-lg border-0 stateassets-card">
                <Card.Header className="bg-white py-3 stateassets-header">
                  <div className="header-left">
                    <h4 className="mb-1">Assets Summary by State</h4>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      State wise asset-type counts. Click <b>View</b> to see detailed list (all users).
                    </div>
                  </div>

                  <div className="header-right">
                    <Form.Control
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search state..."
                      className="summary-search"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={fetchSummary}
                      disabled={pending}
                    >
                      Refresh
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body className="p-0">
                  {notUpdated.length > 0 && (
  <Card className="shadow border-0 mb-4" style={{ borderLeft: "4px solid #dc2626" }}>
    <Card.Header style={{ background: "#fee2e2", color: "#dc2626" }}>
      <b>⚠ {notUpdated.length} Regions — No updates after mail was sent</b>
      {lastSentAt && (
        <span className="text-muted ms-2" style={{ fontSize: 12 }}>
          (Mail sent on: {new Date(lastSentAt).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })})
        </span>
      )}
    </Card.Header>
    <Card.Body className="p-0">
      <Table bordered hover className="mb-0">
        <thead style={{ background: "#fef2f2" }}>
          <tr>
            <th>#</th>
            <th>Action</th>
            <th>Region</th>
            <th>Employee Name</th>
            <th>Email</th>
            <th>Last Update</th>
            <th>Mail Sent Count</th>
          </tr>
        </thead>
        <tbody>
          {notUpdated.map((r, i) => (
            <tr key={r.username}>
              <td>{i + 1}</td>
              <td className="text-center">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => sendIndividualReminder(r.username, r.EmployeeName)}
          disabled={sendingEmail === r.username}
          title={`Send reminder to ${r.EmployeeName}`}
          style={{margin:"0px",padding:"0px" , background:"white" , }}
        >
          {sendingEmail === r.username ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="bi bi-envelope" style={{color:"blue" , fontSize:"21px"}}></i> // Bootstrap Icons
          )}
        </Button>
      </td>
              <td className="fw-semibold">{r.Areaoffice}</td>
              <td>{r.EmployeeName}</td>
              <td>{r.email}</td>
              <td className="text-danger">
                {r.last_updated
                  ? new Date(r.last_updated).toLocaleDateString("en-IN")
                  : "Never updated"}
              </td>
              <td className="text-center">
  <span
    className="badge"
    style={{
      background: r.mail_trigger_count > 0 ? "#1d4ed8" : "#9ca3af",
      fontSize: "12px",
      padding: "6px 10px",
    }}
    title={
      r.last_mail_sent_at
        ? `Last sent: ${new Date(r.last_mail_sent_at).toLocaleString("en-IN")}`
        : "No reminder sent yet"
    }
  >
    {r.mail_trigger_count || 0}
  </span>
</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card.Body>
  </Card>
)}
                  {error && (
                    <div className="p-3">
                      <Alert variant="danger" className="mb-0">
                        {error}
                      </Alert>
                    </div>
                  )}

                  {pending ? (
                    <div className="summary-loader">
                      <Spinner animation="border" />
                      <div className="mt-2 text-muted">Loading summary…</div>
                    </div>
                  ) : (
                    <div className="stateassets-table-wrap">
                      <Table bordered hover className="mb-0 stateassets-table">
                        <thead>
  <tr>
    <th className="col-state">State</th>
    {types.map((t) => (
      <th key={t} className="col-type text-center">
        {assetTypeDisplayNames[t] || t.split(' ').map((word, idx) => (
          <React.Fragment key={idx}>
            {word}
            {idx < t.split(' ').length - 1 && <br />}
          </React.Fragment>
        ))}
      </th>
    ))}
  </tr>
</thead>

                        <tbody>
                          {filteredStates.length === 0 ? (
                            <tr>
                              <td
                                colSpan={types.length + 2}
                                className="text-center text-muted py-4"
                              >
                                No state found
                              </td>
                            </tr>
                          ) : (
                            filteredStates.map((r) => (
                              <tr key={r.state}>
                                <td
  className="fw-semibold col-state"
  style={{
    cursor: "pointer",
    color: "#0d6efd",
    textDecoration: "underline",
  }}
  onClick={() => onView(r.state)}
>
  {r.state}
</td>

                                {types.map((t) => (
                                  <td key={t} className="text-center col-type">
                                    {Number(r.counts?.[t] || 0)}
                                  </td>
                                ))}

                                {/* <td className="text-center col-view">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => onView(r.state)}
                                  >
                                    View
                                  </Button>
                                </td> */}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default StateAssetsSummary;
